import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, doc, updateDoc, getDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import YapperModal from '../components/YapperModal';
import './ReportsView.css';

function ReportsView({ user }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isWorker, setIsWorker] = useState(false);
  const [isStaff, setIsStaff] = useState(false);
  const [showTimeoutModal, setShowTimeoutModal] = useState(false);
  const [timeoutDuration, setTimeoutDuration] = useState('');
  const [sortOrder, setSortOrder] = useState('newest'); // 'newest' or 'oldest'
  const [modal, setModal] = useState({ isOpen: false, title: '', message: '', type: 'info', onConfirm: null, showCancel: false });
  const reportDetailRef = React.useRef(null);

  const showAlert = (title, message, type = 'info') => {
    setModal({ isOpen: true, title, message, type, onConfirm: () => setModal(m => ({ ...m, isOpen: false })), showCancel: false });
  };

  const showConfirm = (title, message, onConfirmCallback, type = 'warning') => {
    setModal({
      isOpen: true,
      title,
      message,
      type,
      onConfirm: () => {
        setModal(m => ({ ...m, isOpen: false }));
        if (onConfirmCallback) onConfirmCallback();
      },
      showCancel: true
    });
  };

  useEffect(() => {
    checkStaffStatus();
  }, [user]);

  useEffect(() => {
    if (isStaff) {
      loadReports();
    }
  }, [isStaff, sortOrder]);

  // Add mouse wheel scroll functionality for report details
  useEffect(() => {
    const handleWheel = (e) => {
      e.preventDefault();
      const scrollAmount = e.deltaY;
      if (reportDetailRef.current) {
        reportDetailRef.current.scrollBy({
          top: scrollAmount,
          behavior: 'smooth'
        });
      }
    };

    const container = reportDetailRef.current;
    if (container && selectedReport) {
      container.addEventListener('wheel', handleWheel, { passive: false });

      return () => {
        container.removeEventListener('wheel', handleWheel);
      };
    }
  }, [selectedReport]);

  const checkStaffStatus = async () => {
    try {
      const userId = localStorage.getItem('userId');
      const adminDoc = await getDoc(doc(db, 'admins', userId));
      if (adminDoc.exists()) {
        setIsAdmin(true);
        setIsWorker(false);
        setIsStaff(true);
      } else {
        // Check if worker
        const workerDoc = await getDoc(doc(db, 'workers', userId));
        if (workerDoc.exists()) {
          setIsWorker(true);
          setIsAdmin(false);
          setIsStaff(true);
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('Error checking staff status:', error);
      setLoading(false);
    }
  };

  const loadReports = async () => {
    try {
      const reportsQuery = query(collection(db, 'reports'));
      const reportsSnapshot = await getDocs(reportsQuery);

      const reportsList = [];
      for (const reportDoc of reportsSnapshot.docs) {
        const reportData = reportDoc.data();

        // Get reporter info
        const reporterDoc = await getDoc(doc(db, 'users', reportData.reporterId));
        const reporterData = reporterDoc.data();
        const reporterPriv = (await getDoc(doc(db, 'userPrivate', reportData.reporterId))).data();

        // Get reported user info
        const reportedDoc = await getDoc(doc(db, 'users', reportData.reportedId));
        const reportedData = reportedDoc.data();
        const reportedPriv = (await getDoc(doc(db, 'userPrivate', reportData.reportedId))).data();

        reportsList.push({
          id: reportDoc.id,
          ...reportData,
          reporter: {
            id: reportData.reporterId,
            username: reporterData?.username || 'Unknown',
            email: reporterPriv?.email || reporterData?.email || 'N/A'
          },
          reported: {
            id: reportData.reportedId,
            username: reportedData?.username || 'Unknown',
            email: reportedPriv?.email || reportedData?.email || 'N/A',
            isBanned: reportedData?.isBanned || false
          }
        });
      }

      // Sort by timestamp based on sort order
      reportsList.sort((a, b) => {
        const timeA = a.timestamp?.seconds || 0;
        const timeB = b.timestamp?.seconds || 0;
        return sortOrder === 'newest' ? timeB - timeA : timeA - timeB;
      });

      setReports(reportsList);
    } catch (error) {
      console.error('Error loading reports:', error);
    }
  };

  const handleBanUser = async (userId, username) => {
    showConfirm('Ban User', `Are you sure you want to ban ${username}? This action cannot be undone.`, async () => {
      try {
        await updateDoc(doc(db, 'users', userId), {
          isBanned: true,
          bannedAt: new Date()
        });

        showAlert('User Banned', `User ${username} has been banned successfully.`, 'success');
        loadReports(); // Reload reports to update banned status
        setSelectedReport(null);
      } catch (error) {
        console.error('Error banning user:', error);
        showAlert('Error', 'Failed to ban user. Please try again.', 'error');
      }
    });
  };

  const handleResolveReport = async (reportId) => {
    try {
      await updateDoc(doc(db, 'reports', reportId), {
        status: 'resolved',
        resolvedAt: new Date()
      });

      showAlert('Report Resolved', 'Report marked as resolved.', 'success');
      loadReports();
      setSelectedReport(null);
    } catch (error) {
      console.error('Error resolving report:', error);
      showAlert('Error', 'Failed to resolve report.', 'error');
    }
  };

  const handleForgiveReport = async (reportId) => {
    showConfirm('Forgive Report', 'Are you sure you want to forgive this report? It will be marked as dismissed.', async () => {
      try {
        const reportRef = doc(db, 'reports', reportId);

        await updateDoc(reportRef, {
          status: 'dismissed',
          dismissedAt: new Date(),
          dismissedBy: localStorage.getItem('userId')
        });

        showAlert('Report Dismissed', 'Report has been dismissed.', 'success');
        loadReports();
        setSelectedReport(null);
      } catch (error) {
        console.error('Error dismissing report:', error);
        showAlert('Error', `Failed to dismiss report: ${error.message}`, 'error');
      }
    });
  };

  const handleTimeoutUser = async () => {
    if (!timeoutDuration || timeoutDuration <= 0) {
      showAlert('Invalid Duration', 'Please enter a valid timeout duration.', 'warning');
      return;
    }

    const odUserId = selectedReport.reported.id;
    const username = selectedReport.reported.username;

    showConfirm('Timeout User', `Timeout ${username} for ${timeoutDuration} hours?`, async () => {
      try {
        const timeoutUntil = new Date();
        timeoutUntil.setHours(timeoutUntil.getHours() + parseInt(timeoutDuration));

        // Update user document
        const userRef = doc(db, 'users', odUserId);
        await updateDoc(userRef, {
          isTimedOut: true,
          timeoutUntil: timeoutUntil,
          timedOutAt: new Date(),
          timedOutBy: localStorage.getItem('userId')
        });

        // Update report document
        const reportRef = doc(db, 'reports', selectedReport.id);
        await updateDoc(reportRef, {
          status: 'resolved',
          resolvedAt: new Date(),
          action: 'timeout',
          timeoutDuration: parseInt(timeoutDuration),
          resolvedBy: localStorage.getItem('userId')
        });

        showAlert('User Timed Out', `User ${username} has been timed out for ${timeoutDuration} hours.`, 'success');
        setShowTimeoutModal(false);
        setTimeoutDuration('');
        loadReports();
        setSelectedReport(null);
      } catch (error) {
        console.error('Error timing out user:', error);
        showAlert('Error', `Failed to timeout user: ${error.message}`, 'error');
      }
    });
  };

  const handlePermanentBan = async (odUserId, username, email) => {
    const confirmation = window.prompt(
      `⚠️ PERMANENT BAN WARNING ⚠️\n\nYou are about to PERMANENTLY BAN:\nUsername: ${username}\nEmail: ${email}\n\nThis will:\n- Ban the user's account permanently\n- Block their IP address\n- Block their email address\n- Prevent them from creating new accounts\n\nType "CONFIRM BAN" to proceed:`
    );

    if (confirmation !== 'CONFIRM BAN') {
      return;
    }

    try {
      // Get user's IP address from their profile
      const userDoc = await getDoc(doc(db, 'users', odUserId));
      const userData = userDoc.data();
      const ipAddress = userData?.ipAddress || null;

      // Ban the user account
      const userRef = doc(db, 'users', odUserId);
      await updateDoc(userRef, {
        isBanned: true,
        isPermanentBan: true,
        bannedAt: new Date(),
        bannedReason: selectedReport.reason,
        bannedBy: localStorage.getItem('userId')
      });

      // Update report status
      const reportRef = doc(db, 'reports', selectedReport.id);
      await updateDoc(reportRef, {
        status: 'resolved',
        resolvedAt: new Date(),
        action: 'permanent_ban',
        resolvedBy: localStorage.getItem('userId')
      });

      showAlert('User Permanently Banned', `User ${username} has been permanently banned.\n\n${ipAddress ? 'IP: ' + ipAddress + ' blocked' : 'No IP address found'}\nEmail: ${email} blocked`, 'success');
      loadReports();
      setSelectedReport(null);
    } catch (error) {
      console.error('Error permanently banning user:', error);
      showAlert('Error', `Failed to permanently ban user: ${error.message}`, 'error');
    }
  };

  const handleDeleteReport = async (reportId) => {
    showConfirm('Delete Report', 'Are you sure you want to permanently delete this report? This action cannot be undone.', async () => {
      try {
        const reportRef = doc(db, 'reports', reportId);
        await deleteDoc(reportRef);

        showAlert('Report Deleted', 'Report has been permanently deleted.', 'success');
        loadReports();
        setSelectedReport(null);
      } catch (error) {
        console.error('Error deleting report:', error);
        showAlert('Error', `Failed to delete report: ${error.message}`, 'error');
      }
    });
  };

  if (loading) {
    return (
      <div className="reports-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (!isStaff) {
    return (
      <div className="reports-container">
        <div className="access-denied">
          <h2>🚫 Access Denied</h2>
          <p>You do not have permission to access this page.</p>
        </div>
      </div>
    );
  }

  if (selectedReport) {
    return (
      <div className="reports-container" ref={reportDetailRef}>
        <button className="back-btn" onClick={() => setSelectedReport(null)}>
          ← Back to Reports
        </button>

        <div className="report-detail">
          <h2>📋 Report Details</h2>

          <div className="report-detail-grid">
            <div className="report-section reporter-section">
              <h3>👤 Reporter</h3>
              <div className="user-info">
                <p><strong>Username:</strong> {selectedReport.reporter.username}</p>
                <p><strong>Email:</strong> {selectedReport.reporter.email}</p>
                <p><strong>User ID:</strong> {selectedReport.reporter.id}</p>
              </div>

              <div className="report-reason">
                <h4>Reason for Report:</h4>
                <p className="reason-text">{selectedReport.reason}</p>
              </div>

              <div className="report-meta">
                <p><strong>Report Date:</strong> {selectedReport.timestamp?.toDate().toLocaleString()}</p>
                <p><strong>Status:</strong> <span className={`status-badge ${selectedReport.status}`}>{selectedReport.status}</span></p>
              </div>
            </div>

            <div className="report-section reported-section">
              <h3>⚠️ Reported User</h3>
              <div className="user-info">
                <p><strong>Username:</strong> {selectedReport.reported.username}</p>
                <p><strong>Email:</strong> {selectedReport.reported.email}</p>
                <p><strong>User ID:</strong> {selectedReport.reported.id}</p>
                <p><strong>Banned:</strong> {selectedReport.reported.isBanned ? '✅ Yes' : '❌ No'}</p>
              </div>

              <div className="chat-history">
                <h4>💬 Chat History</h4>
                <div className="messages-log">
                  {selectedReport.chatHistory && selectedReport.chatHistory.length > 0 ? (
                    selectedReport.chatHistory.map((msg, index) => (
                      <div
                        key={index}
                        className={`chat-message ${msg.senderIsReporter ? 'reporter-msg' : 'reported-msg'}`}
                      >
                        <div className="message-header">
                          <span className="message-sender">
                            {msg.senderIsReporter ? selectedReport.reporter.username : selectedReport.reported.username}
                          </span>
                          <span className="message-time">
                            {new Date(msg.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        <div className="message-text">{msg.text}</div>
                      </div>
                    ))
                  ) : (
                    <p className="no-messages">No chat history available</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="report-actions">
            <button
              className="btn-forgive"
              onClick={() => handleForgiveReport(selectedReport.id)}
            >
              ✨ Forgive
            </button>

            {!selectedReport.reported.isBanned && (
              <>
                <button
                  className="btn-timeout"
                  onClick={() => setShowTimeoutModal(true)}
                >
                  ⏰ Timeout
                </button>

                <button
                  className="btn-permanent-ban"
                  onClick={() => handlePermanentBan(
                    selectedReport.reported.id,
                    selectedReport.reported.username,
                    selectedReport.reported.email
                  )}
                >
                  🔨 Permanent Ban
                </button>
              </>
            )}

            <button
              className="btn-delete-report"
              onClick={() => handleDeleteReport(selectedReport.id)}
            >
              🗑️ Delete Report
            </button>
          </div>
        </div>

        {showTimeoutModal && (
          <div className="modal-overlay" onClick={() => setShowTimeoutModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>⏰ Timeout User</h3>
              <p>Enter the duration (in hours) to restrict {selectedReport.reported.username} from chatting with random users:</p>

              <input
                type="number"
                min="1"
                max="8760"
                value={timeoutDuration}
                onChange={(e) => setTimeoutDuration(e.target.value)}
                placeholder="Hours (e.g., 24, 48, 168)"
                className="timeout-input"
              />

              <div className="quick-options">
                <button onClick={() => setTimeoutDuration('24')} className="quick-btn">24h</button>
                <button onClick={() => setTimeoutDuration('48')} className="quick-btn">48h</button>
                <button onClick={() => setTimeoutDuration('168')} className="quick-btn">1 week</button>
                <button onClick={() => setTimeoutDuration('720')} className="quick-btn">30 days</button>
              </div>

              <div className="modal-actions">
                <button className="btn-cancel" onClick={() => setShowTimeoutModal(false)}>
                  Cancel
                </button>
                <button className="btn-confirm" onClick={handleTimeoutUser}>
                  Confirm Timeout
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="reports-container">
      <div className="reports-header">
        <div className="reports-header-left">
          <h1>📊 Reports Dashboard</h1>
          <p className="reports-count">Total Reports: <strong>{reports.length}</strong></p>
        </div>
        <div className="reports-header-right">
          <label htmlFor="sort-order" className="sort-label">Sort by date:</label>
          <select
            id="sort-order"
            className="sort-dropdown"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
          </select>
        </div>
      </div>

      {reports.length === 0 ? (
        <div className="no-reports">
          <p>No reports to review. Great job keeping the community safe!</p>
        </div>
      ) : (
        <div className="reports-list">
          {reports.map(report => (
            <div
              key={report.id}
              className={`report-card ${report.status}`}
              onClick={() => setSelectedReport(report)}
            >
              <div className="report-card-header">
                <span className={`status-badge ${report.status}`}>
                  {report.status}
                </span>
                <span className="report-date">
                  {report.timestamp?.toDate().toLocaleDateString()}
                </span>
              </div>

              <div className="report-card-body">
                <div className="report-users">
                  <div className="report-user">
                    <span className="user-label">Reporter:</span>
                    <span className="user-name">{report.reporter.username}</span>
                  </div>
                  <div className="report-arrow">→</div>
                  <div className="report-user">
                    <span className="user-label">Reported:</span>
                    <span className="user-name">
                      {report.reported.username}
                      {report.reported.isBanned && <span className="banned-badge">🚫 BANNED</span>}
                    </span>
                  </div>
                </div>

                <div className="report-reason-preview">
                  <strong>Reason:</strong> {report.reason}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <YapperModal
        isOpen={modal.isOpen}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        onConfirm={modal.onConfirm}
        onCancel={modal.showCancel ? () => setModal(m => ({ ...m, isOpen: false })) : null}
      />
    </div>
  );
}

export default ReportsView;
